/** @jsxImportSource @emotion/react */
import React from 'react';
import { FormikErrors, FormikProps, useFormik } from 'formik';
import { FormHelperText } from '@mui/material';
import { Selectable } from '@zip/business-components';
import { RadioGroup } from 'components';
import { useMerchantApplicationContext } from 'contexts/MerchantApplication.context';
import {
  MerchantApplicationContextModel,
  PatchType,
  StepComponentProps,
  StepKeyType,
} from 'types/interfaces/sign-up/MerchantApplication.types';
import { Validators } from 'utils';
import { getStepKey, StepIndex } from '../signUpConfig';
import * as styles from './SignUpForm.styles';

type RadioStepValues = Record<string, string | undefined>;

export type SignUpRadioStepConfig<TValues extends RadioStepValues> = {
  stepIndex: StepIndex;
  selectionField: keyof TValues & string;
  initialValues: TValues;
  requiredSelectionMessage: string;
  validate?: (values: TValues) => FormikErrors<TValues>;
};

type UseSignUpRadioStepResult<TValues extends RadioStepValues> = {
  formik: FormikProps<TValues>;
  options: Selectable[];
  submitAttempted: boolean;
  selectionFieldName: keyof TValues & string;
};

const getStepValues = <TValues extends RadioStepValues>(
  stepKey: StepKeyType,
  details: Partial<MerchantApplicationContextModel>
): Partial<TValues> | undefined => {
  return details?.[stepKey] as Partial<TValues> | undefined;
};

export function useSignUpRadioStep<TValues extends RadioStepValues>(
  config: SignUpRadioStepConfig<TValues>,
  options: Selectable[],
  { provideSubmit, serverErrors }: StepComponentProps
): UseSignUpRadioStepResult<TValues> {
  const { stepState, setStepData } = useMerchantApplicationContext();
  const [submitAttempted, setSubmitAttempted] = React.useState(false);
  const stepKey = getStepKey(config.stepIndex);

  const savedValues = getStepValues<TValues>(
    stepKey,
    stepState.stepData?.merchantApplicationDetails
  );

  const formik = useFormik<TValues>({
    initialValues: {
      ...config.initialValues,
      ...(savedValues ?? {}),
    },
    validate: (values) => {
      const errors: FormikErrors<TValues> = {};

      if (!values[config.selectionField]) {
        errors[config.selectionField] = config.requiredSelectionMessage as any;
      }

      if (config.validate) {
        Object.assign(errors, config.validate(values));
      }

      return errors;
    },
    validateOnBlur: false,
    validateOnChange: false,
    validateOnMount: false,
    onSubmit: async (values) => {
      setStepData(stepKey, values, PatchType.SignUpInfo, true);
    },
  });

  React.useEffect(() => {
    if (submitAttempted) {
      formik.validateForm();
    }

    setStepData(stepKey, formik.values, PatchType.SignUpInfo);
  }, [formik.values, submitAttempted, setStepData, stepKey]);

  React.useEffect(() => {
    provideSubmit?.(async () => {
      setSubmitAttempted(true);

      const errors = await formik.validateForm();

      if (Object.keys(errors).length > 0) {
        formik.setTouched(
          Object.keys(formik.values).reduce(
            (acc, key) => ({ ...acc, [key]: true }),
            {}
          ),
          false
        );
        return false;
      }

      await formik.submitForm();
      return true;
    });
  }, [provideSubmit, formik]);

  React.useEffect(() => {
    if (!serverErrors) {
      return;
    }

    Object.entries(serverErrors).forEach(([field, message]) => {
      formik.setFieldError(field, message);
      formik.setFieldTouched(field, true, false);
    });
  }, [serverErrors, submitAttempted]);

  return {
    formik,
    options,
    submitAttempted,
    selectionFieldName: config.selectionField,
  };
}

export function SignUpRadioStepForm<TValues extends RadioStepValues>({
  formik,
  options,
  submitAttempted,
  selectionFieldName,
}: {
  formik: FormikProps<TValues>;
  options: Selectable[];
  submitAttempted: boolean;
  selectionFieldName: keyof TValues & string;
}): JSX.Element {
  return (
    <form css={styles.radioStepForm}>
      <RadioGroup
        name={selectionFieldName}
        options={options}
        label=""
        value={formik.values[selectionFieldName]}
        valueChanged={(e) =>
          formik.setFieldValue(selectionFieldName, e.target.value)
        }
        error={submitAttempted && Boolean(formik.errors[selectionFieldName])}
        fullWidthOptions
      />
      {submitAttempted && formik.errors[selectionFieldName] && (
        <FormHelperText css={styles.helperText}>
          {Validators.renderFieldError(
            true,
            formik.errors[selectionFieldName],
            submitAttempted
          )}
        </FormHelperText>
      )}
    </form>
  );
}
